import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  CardBody,
  Card,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import DataTable from "react-data-table-component";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CSVLink } from "react-csv";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  CheckCircle,
  Search,
  X,
  Download,
  Edit2,
  ExternalLink,
  Copy,
  QrCode,
  Check,
  Eye,
  EyeOff,
  PauseCircle,
  RefreshCw,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import Sidebar from "../components/Sidebar";
import DateRangeInput from "../components/DateRangeInput";
import brand_logo from "../brand-logo.png";
import api from "../api";

// URL Cell Component (unchanged)

const BASE_URL = `${process.env.REACT_APP_BASE_URL_FORM}`
const URLCell = ({ slug, isActive, baseUrl = `${BASE_URL}` }) => {
  const [qrOpen, setQrOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [logoBase64, setLogoBase64] = useState("");
  const fullUrl = slug ? `${baseUrl}${slug}` : "";
  const qrRef = useRef(null); // ✅ Add this ref

  const imageToBase64 = async (url) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Error converting image:", error);
      return null;
    }
  };

  useEffect(() => {
    imageToBase64(brand_logo).then(setLogoBase64);
  }, []);

  const handleCopy = async (e) => {
    e.stopPropagation();
    if (!slug) return;
    await navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleQRClick = (e) => {
    e.stopPropagation();
    if (slug && isActive) setQrOpen(true);
  };

  const downloadPNG = () => {
    // ✅ Use the ref instead of document.querySelector
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;

    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svg);
    const svgBlob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const size = 600;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, size, size);
      const png = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = png;
      link.download = `QR_${slug}.png`;
      link.click();
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  const BRAND_QR_COLOR = "#0C1B39";

  return (
    <>
      <div className="d-flex align-items-center gap-1" style={{ minWidth: 0 }}>
        <span className="text-truncate flex-grow-1" style={{ fontSize: "0.875rem" }}>
          {slug || "-"}
        </span>
        <div className="d-flex gap-0" style={{ flexShrink: 0 }}>
          <span
            role="button"
            onClick={handleCopy}
            className={`cursor-pointer p-1 ${!slug ? "disabled opacity-50" : ""}`}
            style={{ cursor: slug ? "pointer" : "not-allowed" }}
            title="Copy URL"
          >
            {copied ? <Check size={14} color="#16a34a" /> : <Copy size={14} />}
          </span>

          {isActive && (
            <span
              role="button"
              onClick={handleQRClick}
              className={`cursor-pointer p-1 ${!slug ? "disabled opacity-50" : ""}`}
              style={{ cursor: slug ? "pointer" : "not-allowed" }}
              title="Show QR"
            >
              <QrCode size={14} />
            </span>
          )}
        </div>
      </div>

      <Modal isOpen={qrOpen} toggle={() => setQrOpen(false)} centered size="md">
        <ModalHeader toggle={() => setQrOpen(false)}>Scan Payment URL</ModalHeader>
        <ModalBody className="text-center">
          {/* ✅ Attach the ref here */}
          <div ref={qrRef}>
            {logoBase64 && (
              <QRCodeSVG
                value={fullUrl}
                size={220}
                level="H"
                fgColor={BRAND_QR_COLOR}
                bgColor="#ffffff"
                imageSettings={{ src: logoBase64, width: 36, height: 36, excavate: true }}
              />
            )}
          </div>
          <div className="mt-3">
            <small className="text-muted">{fullUrl}</small>
          </div>
          <Button color="primary" className="mt-3" onClick={downloadPNG}>
            Download QR
          </Button>
        </ModalBody>
      </Modal>
    </>
  );
};

export default function AnalyticsDashboard() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusCounts, setStatusCounts] = useState({
    all: 0,
    success: 0,
    failed: 0,
    pending: 0,
  });
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const searchRef = useRef(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const navigate = useNavigate();

  // View Form Modal States
  const [viewFormModalOpen, setViewFormModalOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    try {
      const token = localStorage.getItem("jwt");
      const response = await api.post("/forms/fetchAllForms", {}, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const result = response.data;
      const processedData = result.reverse().map((item, index) => ({
        ...item,
        displayId: index + 1,
      }));

      setData(processedData);

      setStatusCounts({
        all: result.length,
        success: result.filter((item) => item.isActive === true).length,
        failed: result.filter((item) => item.isActive === false).length,
        customReceipt: result.filter((item) => item.customReceipt === false).length,
        recentlyUpdated: result.filter((item) => {
          const updatedDate = new Date(item.updatedAt);
          const now = new Date();
          const diffInDays = (now - updatedDate) / (1000 * 60 * 60 * 24);
          return diffInDays <= 7; // forms updated in the last 7 days
        }).length,
        pending: 0,
      });


      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const fetchFormDetails = async (slug) => {
    if (!slug) return;

    setViewLoading(true);
    setViewError(null);
    setSelectedForm(null);

    try {
      const token = localStorage.getItem("jwt");
      const response = await api.post(`/forms/view/${slug}`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      setSelectedForm(response.data);
    } catch (err) {
      setViewError(
        err?.response?.data?.message || err.message || "Failed to load form details"
      );
    } finally {
      setViewLoading(false);
    }
  };

  const filteredData = data.filter((row) => {
    const matchesSearch =
      row.title?.toLowerCase().includes(search.toLowerCase()) ||
      row.slug?.toLowerCase().includes(search.toLowerCase()) ||
      row.adminUserName?.toLowerCase().includes(search.toLowerCase()) ||
      row.merchantId?.toString().toLowerCase().includes(search.toLowerCase()) ||
      row.description?.toLowerCase().includes(search.toLowerCase()) ||
      row.displayId?.toString().toLowerCase().includes(search.toLowerCase());

    const matchesStatus =
      status === "all" ||
      (status === "success" && row.isActive === true) ||
      (status === "failed" && row.isActive === false);

    let matchesDate = true;
    if (startDate && endDate && row.createdAt) {
      const rowDate = new Date(row.createdAt);
      const start = new Date(startDate);
      const end = new Date(endDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      matchesDate = rowDate >= start && rowDate <= end;
    }

    return matchesSearch && matchesStatus && matchesDate;
  });

  const csvData = filteredData.map((row) => ({
    id: row.displayId,
    title: row.title || "",
    slug: row.slug || "",
    merchantId: row.merchantId || "",
    description: row.description || "N/A",
    createdAt: row.createdAt ? new Date(row.createdAt).toLocaleString() : "N/A",
    updatedAt: row.updatedAt ? new Date(row.updatedAt).toLocaleString() : "N/A",
    isActive: row.isActive ? "Yes" : "No",
  }));

  const csvHeaders = [
    { label: "ID", key: "id" },
    { label: "Title", key: "title" },
    { label: "Slug", key: "slug" },
    { label: "Merchant ID", key: "merchantId" },
    { label: "Description", key: "description" },
    { label: "Created At", key: "createdAt" },
    { label: "Updated At", key: "updatedAt" },
    { label: "Active", key: "isActive" },
  ];

  const columns = [
    {
      name: "ID",
      selector: (row) => row.displayId,
      sortable: true,
      width: "60px",
    },
    { name: "Title", selector: (row) => row.title, sortable: true, wrap: true, width: "200px" },
    {
      name: "URL",
      sortable: true,
      width: "200px",
      cell: (row) => <URLCell slug={row.slug} isActive={row.isActive} />,
    },
    { name: "Merchant ID", selector: (row) => row.merchantId, sortable: true, width: "135px" },
    { name: "Description", selector: (row) => row.description || "N/A", sortable: true, width: "300px", wrap: true },
    {
      name: "Created At",
      selector: (row) =>
        row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "N/A",
      sortable: true,
      width: "120px",
    },
    {
      name: "Updated At",
      selector: (row) =>
        row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : "N/A",
      sortable: true,
      width: "140px",
    },
    {
      name: "Active",
      selector: (row) => (row.isActive ? "Yes" : "No"),
      sortable: true,
      width: "120px",

    },
    {
      name: "Actions",
      width: "160px",
      cell: (row) => {
        const encoded = localStorage.getItem("role");
        const role = atob(encoded)
        return (
          <div className="d-flex gap-1">
            {/* Show Edit only if not Viewer */}
            {role !== "VIEWER" && (
              <Button
                color="primary"
                size="sm"
                outline
                onClick={() => handleEdit(row)}
                title="Edit Form"
              >
                <Edit2 size={14} />
              </Button>
            )}

            <Button
              color="secondary"
              size="sm"
              outline
              onClick={() => handleOpenInNewTab(row)}
              title="Open Form"
              disabled={!row.isActive}
            >
              <ExternalLink size={14} />
            </Button>

            <Button
              color="primary"
              size="sm"
              outline
              onClick={() => handleViewDetails(row)}
              title="View Details"
            >
              <Eye size={14} />
            </Button>
          </div>
        );
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      center: true,
    }

  ];

  const handleEdit = (row) => {
    navigate(`/builder/${row.slug}`, { state: { isActive: row.isActive } });
  };

  const handleViewDetails = (row) => {
    fetchFormDetails(row.slug);
    setViewFormModalOpen(true);
    setShowPassword(false);
  };

  const handleOpenInNewTab = (row) => {
    localStorage.setItem("isActive", row.isActive);
    window.open(`/forms/${row.slug}`, "_blank", "noopener,noreferrer");
  };

  const setRange = (update) => {
    setStartDate(update[0]);
    setEndDate(update[1]);
  };

  const closeViewModal = () => {
    setViewFormModalOpen(false);
    setSelectedForm(null);
    setViewError(null);
    setShowPassword(false);
  };

  if (loading) {
    return (
      <div className="px-shell">
        <Sidebar />
        <main className="px-main">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
            <Spinner color="primary" />
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-shell">
        <Sidebar />
        <main className="px-main">
          <div className="alert alert-danger">Error: {error}</div>
        </main>
      </div>
    );
  }

  return (
    <div className="px-shell">
      <Sidebar />

      <main className="px-main">
        <div className="px-page-head mb-4">
          <h2 className="px-title">Forms</h2>
          <div className="px-sub">Manage your forms with a clean datatable</div>
        </div>

        <Container fluid className="overflow-auto">
          <Container fluid className="p-0">
            <Row className="mb-4">
              {/* Summary Cards - unchanged */}
              <Col md="3">
                <div className="px-summary-item">
                  <div className="px-summary-icon"><FileText size={18} /></div>
                  <div><span>Total Forms</span><strong>{statusCounts.all}</strong></div>
                </div>
              </Col>
              <Col md="3">
                <div className="px-summary-item">
                  <div className="px-summary-icon"><CheckCircle size={18} color="#16a34a" /></div>
                  <div><span>Active Forms</span><strong>{statusCounts.success}</strong></div>
                </div>
              </Col>
              <Col md="3">
                <div className="px-summary-item">
                  <div className="px-summary-icon"><PauseCircle size={18} color="#f59e0b" /></div>
                  <div><span>Inactive Forms</span><strong>{statusCounts.failed}</strong></div>
                </div>
              </Col>
              <Col md="3">
                <div className="px-summary-item">
                  <div className="px-summary-icon"><RefreshCw size={18} color="#0ea5e9" /></div>
                  <div><span>Recently Updated Forms</span><strong>{statusCounts.recentlyUpdated}</strong></div>
                </div>
              </Col>
            </Row>

            {/* Filters & Export - unchanged */}
            <Row className="align-items-center mb-2 mt-5">
              <Col className="d-flex gap-2 align-items-center flex-wrap">
                <div className={`px-filter px-filter-compact ${search ? "has-value" : ""}`}>
                  <Search size={16} className="px-search-icon" />
                  <input
                    ref={searchRef}
                    className="px-input-compact"
                    placeholder="Search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button type="button" className="px-clear-btn" onClick={() => setSearch("")}>
                      <X size={14} />
                    </button>
                  )}
                </div>

                <DatePicker
                  selectsRange
                  startDate={startDate}
                  endDate={endDate}
                  onChange={setRange}
                  maxDate={new Date()}
                  monthsShown={2}
                  popperPlacement="bottom-start"
                  dateFormat="MMM dd, yyyy"
                  customInput={<DateRangeInput startDate={startDate} endDate={endDate} onClear={() => setRange([null, null])} />}
                />

                <div className="px-status-pills">
                  <button className={`px-pill ${status === "all" ? "active" : ""}`} onClick={() => setStatus("all")}>
                    All
                  </button>
                  <button className={`px-pill success ${status === "success" ? "active" : ""}`} onClick={() => setStatus("success")}>
                    Active
                  </button>
                  <button className={`px-pill failed ${status === "failed" ? "active" : ""}`} onClick={() => setStatus("failed")}>
                    Inactive
                  </button>
                </div>
              </Col>

              <Col md="auto" className="ms-auto">
                <CSVLink
                  data={csvData}
                  headers={csvHeaders}
                  filename={`forms_export_${new Date().toISOString().split("T")[0]}.csv`}
                >
                  <button className="px-export-btn">
                    <Download size={16} /> <span>Export CSV</span>
                  </button>
                </CSVLink>
              </Col>
            </Row>

            <Row>
              <Col>
                <Card className="px-card borderless-card">
                  <CardBody className="borderless-card-body">
                    <div className="px-card-head">
                      <h4>Forms Analytics</h4>
                      <span className="px-muted">
                        Showing {filteredData.length} of {data.length} records
                      </span>
                    </div>

                    <DataTable
                      columns={columns}
                      data={filteredData}
                      pagination
                      paginationPerPage={10}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      highlightOnHover
                      responsive
                      noDataComponent={<div style={{ padding: 40, color: "#64748b" }}>No forms found for selected filters</div>}
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </Container>

        {/* View Form Details Modal - Styled same as Merchant Config View */}
        <Modal
          isOpen={viewFormModalOpen}
          toggle={closeViewModal}
          size="xl"
          centered
          style={{ maxWidth: "900px" }}
        >
          <ModalHeader toggle={closeViewModal} className="border-0 pb-0 pt-4 px-5">
            <h3 className="mb-0">View Form Details</h3>
          </ModalHeader>

          <ModalBody className="pt-4 px-5 pb-4">
            {viewLoading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-3">Loading form details...</p>
              </div>
            ) : viewError ? (
              <div className="alert alert-danger">{viewError}</div>
            ) : selectedForm ? (
              <Row className="g-4">
                <Col md={6}>
                  <FormGroup className="mb-0">
                    <Label className="fw-medium mb-2" style={{ fontSize: "0.95rem" }}>Title</Label>
                    <Input
                      value={selectedForm.title || "-"}
                      disabled
                      style={{
                        padding: "0.65rem 0.75rem",
                        fontSize: "0.95rem",
                        backgroundColor: "#f8f9fa",
                        color: "#495057",
                        cursor: "not-allowed",
                      }}
                    />
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup className="mb-0">
                    <Label className="fw-medium mb-2" style={{ fontSize: "0.95rem" }}>Slug</Label>
                    <Input
                      value={selectedForm.slug || "-"}
                      disabled
                      style={{
                        padding: "0.65rem 0.75rem",
                        fontSize: "0.95rem",
                        backgroundColor: "#f8f9fa",
                        color: "#495057",
                        cursor: "not-allowed",
                      }}
                    />
                  </FormGroup>

                </Col>


                <Col md={6}>
                  <FormGroup className="mb-0">
                    <Label className="fw-medium mb-2" style={{ fontSize: "0.95rem" }}>Merchant ID</Label>
                    <Input
                      value={selectedForm.merchantId || "-"}
                      disabled
                      style={{
                        padding: "0.65rem 0.75rem",
                        fontSize: "0.95rem",
                        backgroundColor: "#f8f9fa",
                        color: "#495057",
                        cursor: "not-allowed",
                      }}
                    />
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup className="mb-0">
                    <Label className="fw-medium mb-2" style={{ fontSize: "0.95rem" }}>Form ID</Label>
                    <Input
                      value={selectedForm.formId || "-"}
                      disabled
                      style={{
                        padding: "0.65rem 0.75rem",
                        fontSize: "0.95rem",
                        backgroundColor: "#f8f9fa",
                        color: "#495057",
                        cursor: "not-allowed",
                      }}
                    />
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup className="mb-0">
                    <Label className="fw-medium mb-2" style={{ fontSize: "0.95rem" }}>Description</Label>
                    <Input
                      type="textarea"
                      rows="3"
                      value={selectedForm.description || "-"}
                      disabled
                      style={{
                        padding: "0.65rem 0.75rem",
                        fontSize: "0.95rem",
                        backgroundColor: "#f8f9fa",
                        color: "#495057",
                        cursor: "not-allowed",
                      }}
                    />
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup className="mb-0">
                    <Label className="fw-medium mb-2" style={{ fontSize: "0.95rem" }}>Admin Username</Label>
                    <Input
                      value={selectedForm.adminUserName || "-"}
                      disabled
                      style={{
                        padding: "0.65rem 0.75rem",
                        fontSize: "0.95rem",
                        backgroundColor: "#f8f9fa",
                        color: "#495057",
                        cursor: "not-allowed",
                      }}
                    />
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup className="mb-0">
                    <Label className="fw-medium mb-2" style={{ fontSize: "0.95rem" }}>Custom Receipt</Label>
                    <Input
                      value={selectedForm.customReceipt ? "Yes" : "No"}
                      disabled
                      style={{
                        padding: "0.65rem 0.75rem",
                        fontSize: "0.95rem",
                        backgroundColor: "#f8f9fa",
                        color: "#495057",
                        cursor: "not-allowed",
                      }}
                    />
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup className="mb-0">
                    <Label className="fw-medium mb-2" style={{ fontSize: "0.95rem" }}>Number of Fields</Label>
                    <Input
                      value={selectedForm.form_schema?.fields?.length || 0}
                      disabled
                      style={{
                        padding: "0.65rem 0.75rem",
                        fontSize: "0.95rem",
                        backgroundColor: "#f8f9fa",
                        color: "#495057",
                        cursor: "not-allowed",
                      }}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup className="mb-0">
                    <Label className="fw-medium mb-2" style={{ fontSize: "0.95rem" }}>
                      Selected UDFs Count
                    </Label>

                    <Input
                      value={
                        Object.values(
                          selectedForm?.form_schema?.udfMappings || {}
                        ).filter(v => v?.trim()).length
                      }
                      disabled
                      style={{
                        padding: "0.65rem 0.75rem",
                        fontSize: "0.95rem",
                        backgroundColor: "#f8f9fa",
                        color: "#495057",
                        cursor: "not-allowed",
                      }}
                    />
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup className="mb-0">
                    <Label className="fw-medium mb-2" style={{ fontSize: "0.95rem" }}>Updated At</Label>
                    <Input
                      value={
                        selectedForm.updatedAt
                          ? selectedForm.updatedAt.replace("T", " ")
                          : "-"
                      }
                      disabled

                      style={{
                        padding: "0.65rem 0.75rem",
                        fontSize: "0.95rem",
                        backgroundColor: "#f8f9fa",
                        color: "#495057",
                        cursor: "not-allowed",
                      }}
                    />
                  </FormGroup>

                </Col>


              </Row>
            ) : (
              <p className="text-muted text-center py-4">No form data available</p>
            )}
          </ModalBody>

          <ModalFooter className="border-0 pt-3 pb-4 px-5">
            <Button
              color="primary"
              onClick={closeViewModal}
              style={{ padding: "0.65rem 1.5rem", fontSize: "0.95rem" }}
            >
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </main>
    </div>
  );
}